import { useMemo } from 'react';
import { Dialog, Flex, Text, IconButton, Icon, DataTable, Badge, Link } from '@scale/scaleui-radix';
import { faClose, faCopy, faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { toast } from 'react-toastify';
import { useGetMissionConfigsForCollection } from 'react-scale/components/WebinarSessions/api';
import { WebinarMissionConfigDTO } from '@scale/shared/webinarSessions/types';
import moment from 'moment';

const COLUMN_HELPER = DataTable.createColumnHelper<WebinarMissionConfigDTO>();

interface ExistingMissionsDialogProps {
  collectionId: string;
  toggleModal: () => void;
}

export const ExistingMissionsDialog = ({
  collectionId,
  toggleModal,
}: ExistingMissionsDialogProps) => {
  const { data: missionConfigData, isLoading: isLoadingMissionConfigs } =
    useGetMissionConfigsForCollection({ collectionId });

  const sortedMissionConfigs = useMemo(() => {
    if (!missionConfigData) {
      return [];
    }

    return missionConfigData.missionConfigs.sort(
      (a: WebinarMissionConfigDTO, b: WebinarMissionConfigDTO) => {
        if (moment(a.startAt).isSame(moment(b.startAt))) {
          return moment(b.endsAt).diff(moment(a.endsAt));
        }
        return moment(b.startAt).diff(moment(a.startAt));
      },
    );
  }, [missionConfigData]);

  const COLUMNS = useMemo(
    () => [
      COLUMN_HELPER.accessor('missionConfigId', {
        id: 'missionConfigId',
        header: function headerComponent() {
          return <DataTable.CellContentDefault>Mission Config ID</DataTable.CellContentDefault>;
        },
        filterFn: 'includesStringSensitive',
        cell: function cellComponent(props: any) {
          const id = props.getValue();

          return (
            <Flex
              direction="row"
              gap="2"
              justify="between"
              align="center"
              wrap="wrap"
              className="truncate"
            >
              <Badge>
                <Link
                  href={`/corp/mission-config/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                >
                  {id}
                </Link>
              </Badge>

              <Flex direction="row" gap="2">
                <IconButton
                  variant="ghost"
                  color="neutral"
                  onClick={() => {
                    window.open(`/corp/mission-config/${id}`, '_blank');
                  }}
                >
                  <Icon size="1" icon={faExternalLink} label="Open mission config" />
                </IconButton>
                <IconButton
                  variant="ghost"
                  color="neutral"
                  onClick={() => {
                    navigator.clipboard.writeText(id);
                    toast(`Copied mission config ID (${id}) to clipboard!`, {
                      progressStyle: { background: 'green' },
                    });
                  }}
                >
                  <Icon size="1" icon={faCopy} label="Copy mission config ID" />
                </IconButton>
              </Flex>
            </Flex>
          );
        },
      }),
      COLUMN_HELPER.accessor('approved', {
        id: 'approved',
        size: 75,
        header: function headerComponent() {
          return <DataTable.CellContentDefault>Approved</DataTable.CellContentDefault>;
        },
        filterFn: 'includesString',
        cell: function cellComponent(props: any) {
          const approved = props.getValue();
          return <Text color="neutral">{approved ? 'Yes' : 'No'}</Text>;
        },
      }),
      COLUMN_HELPER.accessor('enabled', {
        id: 'enabled',
        size: 75,
        header: function headerComponent() {
          return <DataTable.CellContentDefault>Enabled</DataTable.CellContentDefault>;
        },
        filterFn: 'includesString',
        cell: function cellComponent(props: any) {
          const enabled = props.getValue();
          return <Text color="neutral">{enabled ? 'Yes' : 'No'}</Text>;
        },
      }),
      COLUMN_HELPER.accessor('startAt', {
        id: 'startAt',
        size: 100,
        header: function headerComponent() {
          return <DataTable.CellContentDefault>Start Time</DataTable.CellContentDefault>;
        },
        cell: function cellComponent(props: any) {
          const startAt = props.getValue();
          return <Text color="neutral">{moment(startAt).format('MM/DD/YYYY HH:mm')}</Text>;
        },
      }),
      COLUMN_HELPER.accessor('endsAt', {
        id: 'endsAt',
        size: 100,
        header: function headerComponent() {
          return <DataTable.CellContentDefault>End Time</DataTable.CellContentDefault>;
        },
        cell: function cellComponent(props: any) {
          const endsAt = props.getValue();
          return <Text color="neutral">{moment(endsAt).format('MM/DD/YYYY HH:mm')}</Text>;
        },
      }),
    ],
    [],
  );

  return (
    <Dialog.Root open={true} onOpenChange={toggleModal}>
      <Dialog.Content size="1" width="fit-content" maxWidth="70vw" maxHeight="70vh">
        <Flex direction="column" gap="4">
          <Flex justify="between">
            <Text size="4">Missions for this Webinar</Text>

            <Dialog.Close>
              <IconButton variant="ghost" color="neutral">
                <Icon label="Close" icon={faClose} size="2" />
              </IconButton>
            </Dialog.Close>
          </Flex>

          <Text>
            The following missions are currently tied to this webinar collection. Click on the link
            to view a mission config and reprocess the mission as needed. If you do not have
            permissions to reprocess missions, reach out to the Pay Ops team.
          </Text>

          <DataTable.Root
            columns={COLUMNS}
            data={sortedMissionConfigs}
            loading={isLoadingMissionConfigs}
            enableSorting={true}
            enableFilters={true}
            initialState={{
              pagination: {
                pageSize: 5,
              },
            }}
            getRowId={row => row.missionConfigId}
          >
            {({ table }) => (
              <>
                <DataTable.Table>
                  <DataTable.Header>
                    {table.getHeaderGroups().map(headerGroup => (
                      <DataTable.HeaderGroup key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <DataTable.ColumnHeaderCell key={header.id} header={header}>
                            {header.column.getCanSort() ? (
                              <DataTable.ColumnHeaderSort header={header} />
                            ) : null}
                          </DataTable.ColumnHeaderCell>
                        ))}
                      </DataTable.HeaderGroup>
                    ))}
                  </DataTable.Header>
                  <DataTable.Body>
                    {table.getRowModel().rows.map(row => (
                      <DataTable.Row key={row.id} row={row}>
                        {row.getVisibleCells().map(cell => (
                          <DataTable.Cell key={cell.id} cell={cell} />
                        ))}
                      </DataTable.Row>
                    ))}
                  </DataTable.Body>
                </DataTable.Table>
                <DataTable.Pagination>
                  <DataTable.PaginationPageNavigation />
                  <DataTable.PaginationPageSize />
                  <DataTable.PaginationTotal />
                </DataTable.Pagination>
              </>
            )}
          </DataTable.Root>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
